import { useEffect, useState } from "react"
import {
  Dialog,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  useMediaQuery,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { useTranslation } from "react-i18next"
import { useAuthContext } from "contexts/AuthContext"
import { maxWidth600 } from "themes/default/breakpoints"
import useAIndependentWorkApi from "modules/independentWork"
import useRealTimeInstructionsApi from "modules/realTimeInstruction"
import { NOTIFICATION_TYPES } from "common/constants"
import { styles } from "./SharedDeviceModal.styles"

export default function SharedDeviceModal({
  open = false,
  onClose,
  reconnectId = 0,
  teamData,
}) {
  const { t } = useTranslation()
  const [students, setStudents] = useState([])
  const { selectedGroupData, userData } = useAuthContext()
  const { getTeamMembers, addTeamMember, removeTeamMember } =
    useAIndependentWorkApi()
  const {
    getTeamMembers: getGameTeamMembers,
    addTeamMember: addGameTeamMember,
    removeTeamMember: removeGameTeamMember,
  } = useRealTimeInstructionsApi()
  const isMobile = useMediaQuery(maxWidth600)

  useEffect(() => {
    if (!open) {
      setStudents([])
    }
    return () => {
      setStudents([])
    }
  }, [open])

  useEffect(() => {
    if (
      teamData === null ||
      !open ||
      !selectedGroupData?.groupId ||
      selectedGroupData?.groupId < 0
    ) {
      setStudents([])
      return
    }
    if (
      !teamData?.teamMember?.independentWorkId &&
      !teamData?.teamMember?.gameId
    ) {
      setStudents([])
      console.warn("no team data")
      return
    }
    switch (teamData?.notificationType) {
      case NOTIFICATION_TYPES.SMASH_GAME:
        getGameTeamMembers({
          groupId: selectedGroupData?.groupId,
          gameId: teamData?.teamMember?.gameId,
        }).then((response) => {
          setStudents(response || [])
        })
        break
      case NOTIFICATION_TYPES.INDEPENDENT_WORK:
        getTeamMembers({
          groupdId: selectedGroupData?.groupId,
          independentWorkId: teamData?.teamMember?.independentWorkId,
        }).then((response) => {
          setStudents(response || [])
        })
        break
      default:
        break
    }
  }, [open, teamData, reconnectId])

  const handleMemberClick = async (item) => {
    const { userId } = item
    const { independentWorkId, teamId, gameId, groupId } = teamData?.teamMember
    const myTeam = students.filter(
      (item) => item.teamId > 0 && item.teamId === teamData?.teamMember?.teamId
    )
    const newStudents = [...students]
    switch (teamData?.notificationType) {
      case NOTIFICATION_TYPES.SMASH_GAME:
        if (myTeam.find((m) => m.userId === item.userId)) {
          await removeGameTeamMember({
            groupId,
            gameId,
            teamId,
            userId: userId,
          })
        } else {
          const response = await addGameTeamMember({
            groupId,
            gameId,
            teamId,
            userId: userId,
          })
          const studentIndex = newStudents.findIndex(
            (student) => student.userId === userId
          )
          if (!response?.status) {
            newStudents[studentIndex] = {
              ...newStudents[studentIndex],
              disabled: true,
            }
            setStudents(newStudents)
          }
        }
        break
      case NOTIFICATION_TYPES.INDEPENDENT_WORK:
        if (myTeam.find((m) => m.userId === item.userId)) {
          await removeTeamMember({ independentWorkId, teamId, userId: userId })
        } else {
          const response = await addTeamMember({
            independentWorkId,
            teamId,
            userId: userId,
          })
          if (!response?.status) {
            const studentIndex = newStudents.findIndex(
              (student) => student.userId === userId
            )
            newStudents[studentIndex] = {
              ...newStudents[studentIndex],
              disabled: true,
            }
            setStudents(newStudents)
          }
        }
        break
      default:
        break
    }
  }

  const isSelected = (item) => {
    return (
      item.teamId > 0 &&
      item.teamId === teamData?.teamMember?.teamId &&
      item.userId !== userData?.userId
    )
  }

  const isCurrentUser = (memberId) => {
    return memberId === userData?.userId
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: styles.paper,
      }}
    >
      <DialogTitle component="div">
        <Stack sx={styles.header} direction={"row"}>
          <Typography variant={"span"} sx={styles.selectTeamText} fontSize={20}>
            {t("select_your_team_members")}
          </Typography>
          <Box sx={styles.headerRight}>
            <Typography
              variant="span"
              sx={styles.currentUserNameText}
              fontSize={16}
            >
              {`${userData?.name || ""} ${userData?.lastName || ""}`}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <IconButton onClick={() => onClose(false)} sx={styles.backButton}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={styles.studentsListContainer}>
          <Stack
            sx={styles.studentList}
            direction={"row"}
            flexWrap={"wrap"}
            justifyContent={"center"}
            alignItems={"stretch"}
            gap={1.25}
          >
            {students.map((member) => {
              const selected = isSelected(member)
              const current = isCurrentUser(member.userId)
              const disabled =
                member.teamId > 0 &&
                member.teamId !== teamData?.teamMember?.teamId
              return (
                <Box
                  key={member.userId}
                  onClick={() => {
                    if (!current && !disabled) {
                      handleMemberClick(member)
                    }
                  }}
                  sx={[
                    styles.teamItem,
                    current && styles.currentUserItem,
                    selected && styles.selectedTeamItem,
                  ]}
                >
                  {selected && !current && (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#4caf50",
                        fontSize: 24,
                        bgcolor: "white",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                  <Avatar
                    src={member.avatar}
                    alt={member.name}
                    sx={styles.teamAvatar}
                  >
                    {member.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography
                    variant="span"
                    sx={styles.teamNameText}
                    fontSize={16}
                  >
                    {`${member.name} ${member.lastName}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={styles.teamUsernameText}
                    fontSize={12}
                  >
                    {member.username}
                  </Typography>
                </Box>
              )
            })}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={styles.actions}>
          <Button
            variant="contained"
            disableElevation
            color={"success"}
            onClick={() => onClose(true)}
            sx={styles.acceptButton}
          >
            {t("Accept")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
